/**
 * Root route (ARCHITECTURE.md §8) — the auth guard + layout switch.
 *
 * `beforeLoad` runs on every navigation and centralizes the guard so each route
 * file stays a trivial page shell: it resolves the session once (cached), and
 * (a) bounces authenticated users away from `/login`, (b) redirects everyone else
 * to `/login` when there is no live session — the redirect target for both the
 * `401` (session lost) and `409 UNCLAIMED` (claim flow) cases described in §7/§10.
 *
 * The component renders the `AppShell` chrome for authenticated routes and a bare
 * `<Outlet>` for `/login`.
 */
import {
  createRootRouteWithContext,
  Outlet,
  redirect,
  useRouterState,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { sessionQuery } from "../lib/query";
import { AppShell } from "../components/AppShell";

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery);
    const onLogin = location.pathname === "/login";

    if (onLogin) {
      if (session.authenticated) {
        throw redirect({ to: "/" });
      }
      return;
    }

    if (!session.authenticated) {
      throw redirect({ to: "/login" });
    }
  },
  component: RootComponent,
});

function RootComponent() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  if (pathname === "/login") {
    return <Outlet />;
  }

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}
